import { Divider } from '@mui/material';
import {
  CustomFieldApplicableTo,
  FilterKeys,
  GetStudentsQueryFilters,
  StudentsArrangeBy,
} from '@schooly/api';
import {
  AgeGroupSelectMultiple,
  ArrangeBySelect,
  CustomFieldsFilters,
  DateSelect,
  FiltersSidebar,
  FiltersSidebarMethods,
  GenderSelectMultiple,
  MedicalNeedFilters,
  NationalitySelectMultiple,
  PropertyTypeSelectMultiple,
  toggleMultipleValueArrayProperty,
  useFiltersSidebar,
} from '@schooly/components/filters';
import { PROPERTIES_TEXT_IDS, SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { useCustomFields } from '@schooly/hooks/use-custom-fields';
import { ExpandableGroup } from '@schooly/style';
import { forwardRef, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { useStudentsFilters } from './useStudentsFilters';

type StudentsFiltersSidebarProps = {
  filters: GetStudentsQueryFilters;
  arrangeBy: StudentsArrangeBy | null;
  defaultFilters: GetStudentsQueryFilters;
  schoolId: string;
  onApply: (v: { filters: GetStudentsQueryFilters; arrangeBy: StudentsArrangeBy | null }) => void;
};

export const StudentsFiltersSidebar = forwardRef<
  FiltersSidebarMethods,
  StudentsFiltersSidebarProps
>(
  (
    { filters: propsFilters, defaultFilters, onApply, schoolId, arrangeBy: propsArrangeBy },
    ref,
  ) => {
    const [filters, setFilters] = useState(propsFilters);
    const [arrangeBy, setArrangeBy] = useState(propsArrangeBy);
    const { allowClose, preventClose, sidebar } = useFiltersSidebar(ref);

    const { activeCustomFields } = useCustomFields({ refetchOnMount: 'always' });

    const showCustomFieldsFilters = !!activeCustomFields?.filter((c) =>
      c.applicable_to.includes(CustomFieldApplicableTo.Student),
    ).length;

    const {
      labels,
      hasHouses,
      filtersHouse,
      filtersGender,
      filtersNationality,
      filtersMedicalNeeds,
      filtersCustomFields,
      noStatusFilterSelected,

      onUpdateCustomFieldFilter,
      onSetNationality,
      onClearNationality,
      onSetGender,
      onClearGender,
      onSetHouseId,
      onClearHouse,
      onSetStatusId,
      onSetNoStatus,
      onClearStatuses,
      onSetMedicalNeeds,

      filtersStatus,
      arrangeByDropdownOptions,
      onSetDate,
      filtersDate,
      filtersAgeGroup,
      onSetAgeGroup,
      onClearAgeGroup,
    } = useStudentsFilters({ schoolId, filters, onSetFilters: setFilters });
    const { formatMessage } = useIntl();
    const handleApply = useCallback(() => {
      onApply({ filters, arrangeBy });
    }, [filters, onApply, arrangeBy]);

    const handleClear = useCallback(() => {
      setFilters(defaultFilters);
      setArrangeBy(null);
    }, [defaultFilters]);

    const renderStatusFilter = () => {
      const commonProps = {
        placeholder: labels.status,
        allowArchived: true,
        userRole: SchoolUserRole.Student,
        schoolId,
        onClear: onClearStatuses,
        propertyType: SchoolPropertyType.Status,
      };

      if (!arrangeBy) {
        return (
          <PropertyTypeSelectMultiple
            {...commonProps}
            onSelectId={(v: string | null) =>
              v === null
                ? // biome-ignore lint/complexity/noUselessTernary: Legacy code
                  onSetNoStatus(noStatusFilterSelected ? false : true)
                : onSetStatusId(toggleMultipleValueArrayProperty(filtersStatus, [v]))
            }
            noneOptionLabel={formatMessage({ id: PROPERTIES_TEXT_IDS[FilterKeys.Expired] })}
            selectedIds={noStatusFilterSelected ? null : filtersStatus || []}
            onClose={allowClose}
            onOpen={preventClose}
          />
        );
      }

      return (
        <PropertyTypeSelectMultiple
          {...commonProps}
          onSelectId={(v: string) =>
            onSetStatusId(toggleMultipleValueArrayProperty(filtersStatus, [v]))
          }
          selectedIds={filtersStatus || []}
          onClose={allowClose}
          onOpen={preventClose}
        />
      );
    };

    const onOpen = useCallback(() => {
      setFilters(propsFilters);
      setArrangeBy(propsArrangeBy);
    }, [propsFilters, propsArrangeBy]);

    return (
      <FiltersSidebar
        onOpen={onOpen}
        ref={sidebar}
        title={formatMessage(
          { id: 'filter-FiltersForPage' },
          { page: formatMessage({ id: 'section-Students' }) },
        )}
        onClear={handleClear}
        onApply={handleApply}
      >
        <DateSelect
          onSetDate={onSetDate}
          date={filtersDate}
          placeholder={labels.date}
          requiredLabel="required"
          emptyValue={formatMessage({ id: 'datepicker-Today' })}
          onClose={allowClose}
          onOpen={preventClose}
        />

        {renderStatusFilter()}

        <AgeGroupSelectMultiple
          allowArchived
          userRole={SchoolUserRole.Student}
          placeholder={labels.age_group}
          schoolId={schoolId}
          onSelect={(v) => onSetAgeGroup(toggleMultipleValueArrayProperty(filtersAgeGroup, v))}
          selectedIds={filtersAgeGroup || []}
          onClear={onClearAgeGroup}
          onClose={allowClose}
          onOpen={preventClose}
        />

        {hasHouses && (
          <PropertyTypeSelectMultiple
            placeholder={labels.house}
            allowArchived
            userRole={SchoolUserRole.Student}
            schoolId={schoolId}
            onSelectId={(v: string) =>
              onSetHouseId(toggleMultipleValueArrayProperty(filtersHouse, [v]))
            }
            selectedIds={filtersHouse || []}
            onClear={onClearHouse}
            propertyType={SchoolPropertyType.House}
            onClose={allowClose}
            onOpen={preventClose}
          />
        )}

        <GenderSelectMultiple
          placeholder={labels.gender}
          onSelectGender={(v) => onSetGender(toggleMultipleValueArrayProperty(filtersGender, [v]))}
          selectedGenders={filtersGender || []}
          onClear={onClearGender}
          onClose={allowClose}
          onOpen={preventClose}
        />

        <NationalitySelectMultiple
          placeholder={labels.nationality}
          onSelectNationality={(v) =>
            onSetNationality(toggleMultipleValueArrayProperty(filtersNationality, [v]))
          }
          selectedNationalities={filtersNationality || []}
          onClear={onClearNationality}
          onClose={allowClose}
          onOpen={preventClose}
        />

        <ExpandableGroup isExpanded={!!filtersMedicalNeeds} title={labels.medical_needs}>
          <MedicalNeedFilters filter={filtersMedicalNeeds} onUpdate={onSetMedicalNeeds} />
        </ExpandableGroup>
        {showCustomFieldsFilters && (
          <ExpandableGroup isExpanded={!!filtersCustomFields?.length} title={labels.custom_fields}>
            <CustomFieldsFilters
              schoolId={schoolId}
              filters={filtersCustomFields}
              onUpdate={onUpdateCustomFieldFilter}
              userRole={CustomFieldApplicableTo.Student}
            />
          </ExpandableGroup>
        )}

        <Divider />

        <ArrangeBySelect
          options={arrangeByDropdownOptions}
          selectedOption={arrangeBy ?? undefined}
          placeholder={labels.arrangeBy}
          onClear={() => setArrangeBy(null)}
          onSelectOption={setArrangeBy}
          onClose={allowClose}
          onOpen={preventClose}
        />
      </FiltersSidebar>
    );
  },
);
